import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { gameHomePath, gameDetailPath } from "../../utils/routes";
import styles from "./GameDetail.module.css";
import Sidebar from "../../Components/Sidebar/Sidebar";
import games from "../../utils/games";
import { post, get } from "../../utils/request";
import {
  getFilePreviewUrl,
  getFilePreviewUrls,
} from "../../utils/attachmentPreview";
import DownloadProgress from "../../Components/DownloadProgress/DownloadProgress";
import AppDownloadModal from "../../Components/AppDownloadModal/AppDownloadModal";
import { useAppDownload } from "../../hooks/useAppDownload";
import { getCurrentUserAvatarUrl } from "../../utils/userAvatar";
import Login from "../../Components/Login/Login";
import CommentSection from "../../Components/CommentSection/CommentSection";
import VoteSection from "../../Components/VoteSection/VoteSection";
import FollowButton from "../../Components/FollowButton/FollowButton";
import { useToast } from "../../Components/Toast/Toast";
import { t } from "../../utils/i18n";
import { BRAND_NAME, GameeBrand } from "../../utils/brand";
import gameeQrLogo from "../../Resources/image/gamee-qr-logo.png";

const ImageWithFallback = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(!src);
  }, [src]);

  return (
    <div className={styles.imageFallbackWrap}>
      {!loaded && <div className={styles.imageFallbackPlaceholder} />}
      {!failed && src ? (
        <img
          src={src}
          alt={alt}
          className={className}
          data-mg-native-fallback
          onLoad={() => setLoaded(true)}
          onError={() => {
            setFailed(true);
            setLoaded(false);
          }}
        />
      ) : null}
    </div>
  );
};

const GameDetail = props => {
  const {
    theme,
    setTheme,
    isLoggedIn,
    handleOpenLogin,
    handleLogout,
    handleOpenProfile,
    cartDisplayed,
    handleCloseCart,
    handleLoginSuccess,
    hoverState,
    handleHover,
  } = props;

  const toast = useToast();
  const {
    openDownloadModal,
    closeDownloadModal,
    startDownload,
    modalOpen,
    modalInfo,
    minimizeDownload,
    expandDownload,
    progress: appDownloadProgress,
    downloading: appDownloading,
  } = useAppDownload({ toast, comingSoonKey: "game.app.comingSoon" });

  const { gameId } = useParams();
  const navigate = useNavigate();
  const SIDEBAR_OPEN_KEY = "mg_sidebar_open";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(() => {
    try {
      return sessionStorage.getItem(SIDEBAR_OPEN_KEY) === "true";
    } catch (e) {
      return false;
    }
  });
  const [activeTab, setActiveTab] = useState("details"); // 'details' or 'reviews'
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [showRequireLoginModal, setShowRequireLoginModal] = useState(false);
  const [mainMediaLightboxOpen, setMainMediaLightboxOpen] = useState(false);

  const [gameDetail, setGameDetail] = useState(null);
  const [relatedGames, setRelatedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaList, setMediaList] = useState([
    "https://via.placeholder.com/640x360/7a5cfa/ffffff?text=Game+Preview",
  ]);
  const [following, setFollowing] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const descRef = useRef(null);
  const [descOverflow, setDescOverflow] = useState(false);
  const thumbnailListRef = useRef(null);
  const userMenuRef = useRef(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userAvatarUrl, setUserAvatarUrl] = useState("");

  // 右侧栏用户头像：登录后拉取当前用户信息并解析头像链接
  useEffect(() => {
    let cancelled = false;

    const fetchUserAvatar = async () => {
      if (!isLoggedIn) {
        setUserAvatarUrl("");
        return;
      }
      try {
        const url = await getCurrentUserAvatarUrl();
        if (!cancelled) setUserAvatarUrl(url || "");
      } catch (e) {
        if (!cancelled) setUserAvatarUrl("");
      }
    };

    fetchUserAvatar();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_OPEN_KEY, String(isMobileMenuOpen));
    } catch (e) {
      // ignore
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const fetchGameDetail = async () => {
      setLoading(true);
      setFollowing(false);
      try {
        const res = await post("/game/game-details", { id: gameId });
        if (res && res.data) {
          const raw = res.data;
          // 封面预览：优先 attachmentUuid；为空则回退 gameIconId
          let gameIconUrl = raw.gameIconUrl || "";
          const previewId = raw.attachmentUuid || raw.gameIconId;
          if (previewId) {
            try {
              gameIconUrl =
                (await getFilePreviewUrl(previewId)) || gameIconUrl;
            } catch (e) {
              // 忽略失败，保留占位
            }
          }
          setGameDetail({ ...raw, gameIconUrl });
          setFollowing(raw.isTracking === true);
        }
      } catch (error) {
        console.error("Failed to fetch game details:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchRelatedGames = async () => {
      try {
        const res = await get("/game/related_game", { id: gameId });
        if (res && res.data) {
          const rawList = Array.isArray(res.data) ? res.data : [];

          // 先用占位符快速渲染列表（仅使用后端返回的 `id`，不再使用 gameCode 兜底）
          const list = rawList
            .filter(item => item?.id != null && String(item.id) !== "")
            .map(item => ({
              id: item.id,
              name: item.gameName,
              // 初始 icon：后续会用 attachmentUuid 调 /api/file/preview 替换
              icon: item.gameIconUrl || "🎮",
              // 优先使用 attachmentUuid，兼容老字段 gameIconId
              iconId: item.attachmentUuid || item.gameIconId,
              rating: item.gameRating || "0.0",
              genre: item.categoryName || "",
              platform: item.platform,
              // producer：相关游戏列表每个 item 都应有自己的 producer
              producer:
                item.producer ||
                item.gameProducer ||
                item.producerName ||
                item.developers ||
                item.developer ||
                item.publisher ||
                item.publisherName ||
                item.maker ||
                "",
            }));
          setRelatedGames(list);

          // 异步根据 iconId 批量补充图标
          list.forEach(game => {
            if (!game.iconId) return;
            getFilePreviewUrl(game.iconId)
              .then(url => {
                if (!url) return;
                setRelatedGames(prev =>
                  prev.map(g => (g.id === game.id ? { ...g, icon: url } : g)),
                );
              })
              .catch(() => {
                // 忽略单个失败，保留占位符
              });
          });
        }
      } catch (error) {
        console.error("Failed to fetch related games:", error);
      }
    };

    if (gameId) {
      fetchGameDetail();
      fetchRelatedGames();
    }
  }, [gameId]);

  // 默认收起描述文案；当内容超出时显示“More/Less”
  useEffect(() => {
    setDescExpanded(false);
  }, [gameId]);

  useEffect(() => {
    const el = descRef.current;
    if (!el) return;
    // 根据内容总高度判断是否需要“More/Less”，与当前展开状态无关
    const id = window.setTimeout(() => {
      const over = el.scrollHeight > 180 + 2; // 对应 descriptionCollapsed 的 max-height
      setDescOverflow(over);
    }, 0);
    return () => window.clearTimeout(id);
  }, [gameDetail]);

  // 从接口获取游戏信息
  const data = gameDetail || {};
  const hyperLinkUrl = String(data.hyperLinkUrl ?? "").trim();
  const isReserveMode = hyperLinkUrl.length > 0;
  const currentGame = {
    id: data.id || gameId,
    name: data.gameName || "Unknown Game",
    cover: data.gameIconUrl || "https://via.placeholder.com/150",
    image: data.gameIconUrl || "https://via.placeholder.com/150",
    genre: data.categoryName || "Action",
    ageRating: data.ageGrading || "12+",
    developers: data.producer || BRAND_NAME,
    desc:
      data.gameDesc ||
      data.introText ||
      "Welcome to the game. Experience the thrill of battle...",
  };

  // 使用详情返回的 attachmentUuid 调用 /api/file/preview，补充大图标地址
  useEffect(() => {
    const iconId = data.attachmentUuid || data.gameIconId;
    if (!iconId) return;

    let cancelled = false;
    getFilePreviewUrl(iconId)
      .then(url => {
        if (cancelled || !url) return;
        // 更新 gameDetail 中的 gameIconUrl，使 currentGame.cover 使用预览图
        setGameDetail(prev => (prev ? { ...prev, gameIconUrl: url } : prev));
      })
      .catch(() => {
        // 出错时保留占位图
      });

    return () => {
      cancelled = true;
    };
  }, [data.attachmentUuid, data.gameIconId]);

  // 根据物料列表异步补充预览图：
  // 优先使用 attachmentUuid 调 /api/file/preview 获取 previewUrl；
  // 如果后端仍返回了 fileUrl，则作为兜底。
  useEffect(() => {
    const materials = Array.isArray(data.materialInfoList)
      ? data.materialInfoList
      : [];

    // 先用已有的 fileUrl 做一次同步展示，避免白屏
    const fileUrlList = materials.map(m => m.fileUrl).filter(Boolean);

    if (fileUrlList.length > 0) {
      setMediaList(fileUrlList);
    }

    // 再根据 attachmentUuid 异步获取真正的预览地址
    const attachmentIds = materials.map(m => m.attachmentUuid).filter(Boolean);

    if (attachmentIds.length > 0) {
      let cancelled = false;
      getFilePreviewUrls(attachmentIds)
        .then(urls => {
          if (cancelled) return;
          const validUrls = (urls || []).filter(Boolean);
          if (validUrls.length > 0) {
            setMediaList(validUrls);
          }
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }

    // 没有任何物料时，才用游戏图标或占位图作为唯一一张图
    if (materials.length === 0 && currentGame.cover) {
      setMediaList([currentGame.cover]);
    }
  }, [data.materialInfoList, currentGame.cover]);

  const handleReserveClick = () => {
    if (!hyperLinkUrl) return;
    window.open(hyperLinkUrl, "_blank", "noopener,noreferrer");
  };

  const parseRating = val => {
    if (val === null || val === undefined) return "0%";
    const num = Number(val);
    if (!Number.isFinite(num)) return "0%";

    // 后端 ratingX 为 0~1 的小数，代表百分比
    const rawPercent = num <= 1 ? num * 100 : num;
    const percent = Math.max(0, Math.min(100, rawPercent));

    return `${percent}%`;
  };

  // 星级分布：ratingX 为 0~1 的小数，代表百分比；没有评分时也显示灰色条
  const ratingBreakdown = [
    { stars: 5, raw: data.rating5, count: parseRating(data.rating5), color: "#22c55e" }, // Green
    { stars: 4, raw: data.rating4, count: parseRating(data.rating4), color: "#86efac" }, // Light Green
    { stars: 3, raw: data.rating3, count: parseRating(data.rating3), color: "#fcd34d" }, // Yellow
    { stars: 2, raw: data.rating2, count: parseRating(data.rating2), color: "#fdba74" }, // Orange
    { stars: 1, raw: data.rating1, count: parseRating(data.rating1), color: "#ea580c" }, // Red
  ];
  const currentScore = data.gameRating
    ? parseFloat(data.gameRating).toFixed(1)
    : "8.5";
  const ratingCount = data.ratingCnt || "0";

  const handlePrevMedia = () => {
    if (!mediaList || mediaList.length === 0) return;
    setActiveMediaIndex(prev =>
      prev === 0 ? mediaList.length - 1 : prev - 1,
    );
  };

  const handleNextMedia = () => {
    if (!mediaList || mediaList.length === 0) return;
    setActiveMediaIndex(prev =>
      prev === mediaList.length - 1 ? 0 : prev + 1,
    );
  };

  useEffect(() => {
    const container = thumbnailListRef.current;
    if (!container) return;
    const selector = `.${styles.thumbnail}.${styles.activeThumb}`;
    const activeThumb = container.querySelector(selector);
    if (!activeThumb) return;

    const containerRect = container.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();

    // 如果当前激活缩略图被遮挡，则平滑滚动使其完全可见
    if (thumbRect.left < containerRect.left) {
      const delta = thumbRect.left - containerRect.left - 8;
      container.scrollBy({ left: delta, behavior: "smooth" });
    } else if (thumbRect.right > containerRect.right) {
      const delta = thumbRect.right - containerRect.right + 8;
      container.scrollBy({ left: delta, behavior: "smooth" });
    }
  }, [activeMediaIndex, mediaList.length]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!mainMediaLightboxOpen) return;
    const onKeyDown = e => {
      if (e.key === "Escape") {
        setMainMediaLightboxOpen(false);
      } else if (e.key === "ArrowLeft") {
        setActiveMediaIndex(prev => {
          if (!mediaList.length) return prev;
          return prev === 0 ? mediaList.length - 1 : prev - 1;
        });
      } else if (e.key === "ArrowRight") {
        setActiveMediaIndex(prev => {
          if (!mediaList.length) return prev;
          return prev === mediaList.length - 1 ? 0 : prev + 1;
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mainMediaLightboxOpen, mediaList.length]);

  const handleBack = () => {
    navigate(gameHomePath());
  };

  if (loading) {
    return (
      <div className={styles.page} data-theme={theme}>
        <div
          className={styles.container}
          style={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            width: "100%",
            height: "100vh",
          }}
        >
          <h2>{t("common.loading", "Loading...")}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} data-theme={theme}>
      {cartDisplayed ? (
        <Login
          cartDisplayed={cartDisplayed}
          handleCloseCart={handleCloseCart}
          onLoginSuccess={handleLoginSuccess}
          handleHover={handleHover}
          hoverState={hoverState}
        />
      ) : null}

      <div className={styles.container}>
        {/* 左侧栏 - Sidebar */}
        <Sidebar
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          theme={theme}
          setTheme={setTheme}
          isLoggedIn={isLoggedIn}
          handleOpenLogin={handleOpenLogin}
        />

        {/* 中间内容 */}
        <main className={styles.mainContent}>
          {/* 顶部标签栏 */}
          <div className={styles.headerBar}>
            <button className={styles.backBtn} onClick={handleBack}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
              <span className={styles.headerTitle}>{t("game.detail.title", "GAME DETAIL")}</span>
            </button>
            <div className={styles.headerActions}>
              <FollowButton
                following={following}
                isLoggedIn={isLoggedIn}
                onRequireLogin={handleOpenLogin}
                onClick={async () => {
                  const res = await post("/user/trace/saveOrCancel", {
                    objectId: String(data.id ?? ""),
                    type: 1,
                  });
                  if (res && res.success) setFollowing(prev => !prev);
                }}
              />
            </div>
          </div>

          {/* 游戏详情内容 - Scrollable */}
          <div
            className={styles.gameDetails}
            onScroll={e => {
              // Optional: Add scroll listener for sticky header effects
            }}
          >
            {/* 1. Media Carousel / Gallery */}
            <div className={styles.carouselSection}>
              <div
                className={styles.mainMedia}
                role="button"
                tabIndex={0}
                aria-label={t("aria.zoomPreview", "Click to zoom preview")}
                onClick={() => setMainMediaLightboxOpen(true)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setMainMediaLightboxOpen(true);
                  }
                }}
              >
                <ImageWithFallback
                  src={mediaList[activeMediaIndex]}
                  alt="Game Preview"
                />
                <div className={styles.mediaOverlay}>
                  {/* Play Button only if video (mocked here as always generic) */}
                  {/* <button className={styles.playBtn}>▶</button> */}
                </div>
              </div>
              <div className={styles.thumbnailBar}>
                {mediaList.length > 1 && (
                  <button
                    type="button"
                    className={`${styles.thumbArrow} ${styles.thumbArrowLeft}`}
                    onClick={handlePrevMedia}
                    aria-label={t("aria.prevScreenshot", "Previous screenshot")}
                  >
                    ‹
                  </button>
                )}
                <div className={styles.thumbnailList} ref={thumbnailListRef}>
                  {mediaList.map((src, idx) => (
                    <div
                      key={idx}
                      className={`${styles.thumbnail} ${activeMediaIndex === idx ? styles.activeThumb : ""}`}
                      onClick={() => setActiveMediaIndex(idx)}
                    >
                      <ImageWithFallback src={src} alt={`Thumb ${idx}`} />
                    </div>
                  ))}
                </div>
                {mediaList.length > 1 && (
                  <button
                    type="button"
                    className={`${styles.thumbArrow} ${styles.thumbArrowRight}`}
                    onClick={handleNextMedia}
                    aria-label={t("aria.nextScreenshot", "Next screenshot")}
                  >
                    ›
                  </button>
                )}
              </div>
            </div>

            {/* 2. Game Info Header (Icon, Title, Download) */}
            <div className={styles.gameHeaderCard}>
              <div className={styles.gameIconLarge}>
                <ImageWithFallback
                  src={currentGame.cover || currentGame.image}
                  alt={currentGame.name}
                />
              </div>
              <div className={styles.gameHeaderInfo}>
                <h1>{currentGame.name}</h1>
                <div className={styles.gameSubMeta}>
                  <span className={styles.platformIcon}>🎮</span>
                  <span>{currentGame.genre}</span>
                  <span className={styles.dot}>•</span>
                  <span className={styles.ageRating}>{currentGame.ageRating}</span>
                </div>
                <div className={styles.gameSubMeta}>
                  <span
                    className={`${styles.platformIcon} ${styles.companyIcon}`}
                    aria-hidden="true"
                  >
                    <svg
                      className={styles.companyIconSvg}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M3 21h18v1H3zm2-2V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v14zm10 0V9a2 2 0 0 1 2-2h2v12zM8 6v2h2V6zm0 4v2h2v-2zm0 4v2h2v-2zm4-8v2h2V6zm0 4v2h2v-2zm0 4v2h2v-2z" />
                    </svg>
                  </span>
                  <span>
                    {String(currentGame.developers) === BRAND_NAME ? (
                      <GameeBrand />
                    ) : (
                      currentGame.developers
                    )}
                  </span>
                </div>
                {/* Optional: Additional badging or logos */}
              </div>
              <div className={styles.gameActions}>
                <button
                  type="button"
                  className={styles.downloadBtn}
                  aria-busy={appDownloading}
                  disabled={appDownloading}
                  onClick={
                    isReserveMode ? handleReserveClick : openDownloadModal
                  }
                >
                  <span className={styles.downloadBtnIcon} aria-hidden="true">
                    {isReserveMode ? (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                    )}
                  </span>
                  {isReserveMode
                    ? t("game.button.reserve", "预注册")
                    : appDownloading
                      ? t("home.downloadApp.loading")
                      : t("game.button.download", "Download")}
                </button>
              </div>
            </div>

            {/* 3. Description & Expand */}
            <div className={styles.descriptionSection}>
              <div
                ref={descRef}
                className={`${styles.descriptionText} ${!descExpanded ? styles.descriptionCollapsed : ""}`}
              >
                <div dangerouslySetInnerHTML={{ __html: currentGame.desc }} />
                {!descExpanded && descOverflow && (
                  <div className={styles.descFade} aria-hidden="true" />
                )}
              </div>
              {descOverflow && (
                <button
                  type="button"
                  className={styles.moreDesc}
                  onClick={() => setDescExpanded(v => !v)}
                >
                  {descExpanded ? t("game.desc.less", "Less") : t("game.desc.more", "More")}
                </button>
              )}
            </div>

            {/* 投票模块：仅当详情接口返回 voteId 时展示 */}
            {data?.voteId != null && data.voteId !== "" && (
              <div style={{ padding: "0 24px" }}>
                <VoteSection
                  voteId={data.voteId}
                  isLoggedIn={isLoggedIn}
                  onRequireLogin={() => {
                    if (typeof handleOpenLogin === "function") handleOpenLogin();
                    else setShowRequireLoginModal(true);
                  }}
                />
              </div>
            )}

            {/* 4. Ratings & Reviews Summary */}
            <div className={styles.ratingsCard}>
              <div className={styles.ratingsHeader}>
                <h3>{t("game.ratings.title", "Ratings & Reviews")}</h3>
              </div>

              <div className={styles.ratingsContent}>
                <div className={styles.totalScore}>
                  <div className={styles.scoreRow}>
                    <div className={styles.scoreValue}>{currentScore}</div>
                    <div className={styles.scoreScale}>/10</div>
                  </div>
                  <div className={styles.totalCount}>
                    {t("game.ratings.count", "{count} Ratings", { count: ratingCount })}
                  </div>
                </div>
                <div className={styles.ratingBars}>
                  {ratingBreakdown.map((item, idx) => (
                    <div key={idx} className={styles.ratingRow}>
                      <div className={styles.starsLabel}>
                        {"★".repeat(item.stars)}
                      </div>
                      <div className={styles.barTrack}>
                        <div
                          className={styles.barFill}
                          style={{
                            // 没有评分时也展示一条满宽的灰色条，有评分时按百分比展示
                            width:
                              item.raw === null ||
                              item.raw === undefined ||
                              Number(item.raw) === 0
                                ? "100%"
                                : item.count,
                            backgroundColor:
                              item.raw === null ||
                              item.raw === undefined ||
                              Number(item.raw) === 0
                                ? "#d4d4d8"
                                : item.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 5. 评论区组件 */}
            <CommentSection
              category="GAME"
              objectId={data.id}
              isLoggedIn={isLoggedIn}
              onRequireLogin={() => setShowRequireLoginModal(true)}
              onRatingUpdated={async () => {
                try {
                  const res = await post("/game/game-details", {
                    id: gameId,
                  });
                  if (res && res.data) {
                    const raw = res.data;
                    setGameDetail(prev => {
                      const next = prev ? { ...prev } : {};
                      return {
                        ...next,
                        gameRating: raw.gameRating,
                        ratingCnt: raw.ratingCnt,
                        rating1: raw.rating1,
                        rating2: raw.rating2,
                        rating3: raw.rating3,
                        rating4: raw.rating4,
                        rating5: raw.rating5,
                      };
                    });
                  }
                } catch (e) {
                  console.warn("刷新评分信息失败:", e);
                }
              }}
              totalFromDetail={data.commentCnt}
            />

            {/* 底部提示 */}
            <div className={styles.emptyState}>
              <p>{t("game.bottom.tip", "That's everything for now. Start a new game?")}</p>
            </div>
          </div>
        </main>

        {/* 右侧栏 */}
        <aside className={styles.rightSidebar}>
          {/* 搜索框 + 用户头像 - 固定在顶部 */}
          <div className={styles.searchSection}>
            <div className={styles.searchBox}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={t("game.search.placeholder", "Games, Topics and Collections")}
              />
            </div>
            {isLoggedIn && handleLogout ? (
              <div className={styles.userArea} ref={userMenuRef}>
                <div
                  className={styles.userAvatar}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-haspopup="true"
                  aria-expanded={showUserMenu}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e =>
                    e.key === "Enter" && setShowUserMenu(v => !v)
                  }
                  aria-label={t("aria.userMenu", "User menu")}
                >
                  <div className={styles.avatar}>
                    {userAvatarUrl ? (
                      <ImageWithFallback src={userAvatarUrl} alt="" />
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    )}
                  </div>
                </div>
                {showUserMenu && (
                  <div className={styles.userMenu}>
                    <button
                      type="button"
                      className={styles.menuItem}
                      onClick={() => {
                        handleOpenProfile?.();
                        setShowUserMenu(false);
                      }}
                    >
                      {t("game.menu.editProfile", "Edit personal information")}
                    </button>
                    <button
                      type="button"
                      className={styles.menuItem}
                      onClick={() => {
                        handleLogout();
                        setShowUserMenu(false);
                      }}
                    >
                      {t("game.menu.logout", "Log out")}
                    </button>
                  </div>
                )}
              </div>
            ) : isLoggedIn ? (
              <div className={styles.userAvatar} aria-label={t("aria.user", "User")}>
                <div className={styles.avatar}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              </div>
            ) : (
              <div
                className={styles.userAvatar}
                onClick={handleOpenLogin}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === "Enter" && handleOpenLogin?.()}
                aria-label={t("aria.logIn", "Log in")}
              >
                <div className={styles.avatarGuest}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* 可滚动内容区 */}
          <div className={styles.scrollableContent}>
            {/* Related Games - Updated List */}
            <div className={styles.relatedGames}>
              <h3>{t("game.right.related", "Related Games")}</h3>
              <div className={styles.gamesList}>
                    {relatedGames.map(game => (
                  // 点击跳转详情页
                  <div
                    key={game.id}
                    className={styles.relatedGameItem}
                    onClick={() => {
                      // 滚动到顶部并跳转
                      window.scrollTo(0, 0);
                      navigate(gameDetailPath(game.id));
                    }}
                  >
                    <div className={styles.relatedGameIcon}>
                      {game.icon && game.icon.length > 2 ? (
                        <ImageWithFallback src={game.icon} alt={game.name} />
                      ) : game.iconId ? null : (
                        game.icon || "🎮"
                      )}
                    </div>
                    <div className={styles.relatedGameInfo}>
                      <div className={styles.relatedGameHeader}>
                        <h3>{game.name}</h3>
                      </div>
                      <div className={styles.relatedGameMeta}>
                        <svg
                          className={styles.icon}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 26 26"
                        >
                          <path
                            fill="currentColor"
                            d="M11.883 3.38c.444-.946 1.79-.946 2.234 0l2.311 4.924c.175.373.524.634.931.696l5.266.804a1.234 1.234 0 0 1 .697 2.083l-3.887 3.984c-.273.28-.398.674-.335 1.06l.907 5.56c.166 1.017-.914 1.778-1.815 1.28l-4.595-2.54a1.235 1.235 0 0 0-1.194 0l-4.595 2.54c-.901.498-1.981-.263-1.815-1.28l.907-5.56a1.234 1.234 0 0 0-.335-1.06l-3.887-3.984a1.234 1.234 0 0 1 .697-2.083L8.64 9a1.23 1.23 0 0 0 .93-.696l2.312-4.923Z"
                          ></path>
                        </svg>
                        <span className={styles.rating}> {game.rating}</span>
                        <span className={styles.dot}>•</span>
                        <span className={styles.platform}>{game.platform}</span>
                      </div>
                      <p className={styles.genre}>{game.genre}</p>
                      <div className={styles.relatedGameProducer}>
                        {game.producer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.aboutGame}>
              <h3>{t("game.right.about", "About the Game")}</h3>
              <div className={styles.infoRow}>
                <span>{t("game.about.provider", "Provider")}</span>
                <span>{data.producer}</span>
              </div>
              <div className={styles.infoRow}>
                <span>{t("game.about.followers", "Followers")}</span>
                <span>{data.traceCnt || "0"}</span>
              </div>
              <div className={styles.infoRow}>
                <span>{t("game.about.downloads", "Downloads")}</span>
                <span>{data.downloadCnt || "0"}</span>
              </div>
              <div className={styles.infoRow}>
                <span>{t("game.about.releaseDate", "Release date")}</span>
                <span>{data.launchDate}</span>
              </div>
            </div>

            <div
              className={`${styles.qrSection} ${appDownloading ? styles.qrSectionDisabled : ""}`}
              role="button"
              tabIndex={appDownloading ? -1 : 0}
              aria-busy={appDownloading}
              aria-disabled={appDownloading}
              onClick={appDownloading ? undefined : openDownloadModal}
              onKeyDown={
                appDownloading
                  ? undefined
                  : e => e.key === "Enter" && openDownloadModal()
              }
            >
              <div className={styles.qrCode}>
                <img
                  src={gameeQrLogo}
                  alt={t("game.qr.title", "Download App")}
                  className={styles.qrLogo}
                />
              </div>
              <div className={styles.qrText}>
                <h4>
                  {appDownloading
                    ? t("home.downloadApp.loading")
                    : t("game.qr.title", "Download App")}
                </h4>
                <p>{t("game.qr.subtitle", "Games worth discovering")}</p>
              </div>
            </div>
          </div>
        </aside>

        {/* 移动端汉堡按钮 */}
        <button
          className={styles.mobileMenuBtn}
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* 未登录提示弹窗 */}
      {showRequireLoginModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowRequireLoginModal(false)}
        >
          <div
            className={styles.modalContent}
            style={{
              width: "400px",
              minHeight: "200px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              className={styles.modalClose}
              onClick={() => setShowRequireLoginModal(false)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <div
              style={{
                fontSize: "20px",
                color: "var(--theme-text-muted, #666)",
                position: "relative",
                zIndex: 1,
              }}
            >
              {t("game.login.prefix", "Please ")}
              <span
                style={{
                  color: "#8b5cf6",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
                onClick={() => {
                  setShowRequireLoginModal(false);
                  // 添加 setTimeout 防止被当前的 React 渲染循环中的状态更新覆盖
                  setTimeout(() => {
                    if (typeof handleOpenLogin === "function") {
                      handleOpenLogin();
                    }
                  }, 0);
                }}
              >
                {t("game.login.link", "Log in")}
              </span>
              {t("game.login.suffix", " first")}
            </div>
          </div>
        </div>
      )}

      {mainMediaLightboxOpen ? (
        <div
          className={styles.mediaLightboxOverlay}
          onClick={() => setMainMediaLightboxOpen(false)}
        >
          <div
            className={styles.mediaLightboxContent}
            onClick={e => e.stopPropagation()}
          >
            {mediaList.length > 1 ? (
              <button
                type="button"
                className={`${styles.mediaLightboxNav} ${styles.mediaLightboxNavLeft}`}
                onClick={e => {
                  e.stopPropagation();
                  handlePrevMedia();
                }}
                aria-label={t("aria.prevImage", "Previous image")}
              >
                ‹
              </button>
            ) : null}
            <button
              type="button"
              className={styles.mediaLightboxClose}
              onClick={() => setMainMediaLightboxOpen(false)}
              aria-label={t("aria.closePreview", "Close preview")}
            >
              ×
            </button>
            <ImageWithFallback
              src={mediaList[activeMediaIndex]}
              alt=""
              className={styles.mediaLightboxImage}
            />
            {mediaList.length > 1 ? (
              <div className={styles.mediaLightboxPager}>
                {activeMediaIndex + 1} / {mediaList.length}
              </div>
            ) : null}
            {mediaList.length > 1 ? (
              <button
                type="button"
                className={`${styles.mediaLightboxNav} ${styles.mediaLightboxNavRight}`}
                onClick={e => {
                  e.stopPropagation();
                  handleNextMedia();
                }}
                aria-label={t("aria.nextImage", "Next image")}
              >
                ›
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <AppDownloadModal
        open={modalOpen}
        loading={modalInfo.loading}
        previewUrl={modalInfo.previewUrl}
        versionName={modalInfo.versionName}
        description={modalInfo.description}
        downloading={appDownloading}
        onClose={closeDownloadModal}
        onDownload={startDownload}
      />
      <DownloadProgress
        open={appDownloadProgress.open}
        minimized={appDownloadProgress.minimized}
        phase={appDownloadProgress.phase}
        percent={appDownloadProgress.percent}
        onMinimize={minimizeDownload}
        onExpand={expandDownload}
      />
    </div>
  );
};

export default GameDetail;
